import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState,
	type ReactNode,
} from 'react';
import {
	ThirdPartyAuthToast,
	type ThirdPartyAuthToastState,
	type ThirdPartyProvider,
} from '@/components/auth/modals/third-party-auth.modal';

type SuccessAuthAction = () => Promise<void> | void;

interface ThirdPartyAuthContextValue {
	onSuccess: (provider: ThirdPartyProvider, afterSuccess?: SuccessAuthAction) => void;
	onError: (message?: string) => void;
	toastState?: ThirdPartyAuthToastState | null;
	onComplete?: () => void;
}

const SUCCESS_AUTH_TOAST_DURATION = 2000;
const ERROR_AUTH_TOAST_DURATION = 3000;

const ThirdPartyAuthContext = createContext<ThirdPartyAuthContextValue | null>(null);

function useThirdPartyAuthState() {
	const [toastState, setToastState] = useState<ThirdPartyAuthToastState | null>(null);
	const afterSuccessRef = useRef<SuccessAuthAction | null>(null);
	const isCompletingRef = useRef(false);

	const complete = useCallback(async () => {
		if (isCompletingRef.current) return;

		isCompletingRef.current = true;
		const action = afterSuccessRef.current;
		setToastState(null);
		afterSuccessRef.current = null;

		try {
			await action?.();
		} catch (error) {
			console.error('Failed to complete third-party auth success flow:', error);
		} finally {
			isCompletingRef.current = false;
		}
	}, []);

	const onSuccess = useCallback(
		(provider: ThirdPartyProvider, afterSuccess?: SuccessAuthAction) => {
			isCompletingRef.current = false;
			afterSuccessRef.current = afterSuccess ?? null;
			setToastState({ status: 'success', provider });
		},
		[]
	);

	const onError = useCallback((message = 'Authentication failed. Please try again.') => {
		isCompletingRef.current = false;
		afterSuccessRef.current = null;
		setToastState({ status: 'error', message });
	}, []);

	useEffect(() => {
		if (!toastState) return;

		const timer = window.setTimeout(
			complete,
			toastState.status === 'success' ? SUCCESS_AUTH_TOAST_DURATION : ERROR_AUTH_TOAST_DURATION
		);

		return () => window.clearTimeout(timer);
	}, [complete, toastState]);

	return { toastState, onSuccess, onError, complete };
}

export function ThirdPartyAuthProvider({ children }: { children: ReactNode }) {
	const { toastState, onSuccess, onError, complete } = useThirdPartyAuthState();

	return (
		<ThirdPartyAuthContext.Provider value={{ onSuccess, onError }}>
			{children}
			<ThirdPartyAuthToast open={!!toastState} toast={toastState} onClose={complete} />
		</ThirdPartyAuthContext.Provider>
	);
}

export function InlineThirdPartyAuthProvider({ children }: { children: ReactNode }) {
	const { toastState, onSuccess, onError, complete } = useThirdPartyAuthState();
	return (
		<ThirdPartyAuthContext.Provider
			value={{ onSuccess, onError, toastState, onComplete: complete }}
		>
			{children}
		</ThirdPartyAuthContext.Provider>
	);
}

export function useThirdPartyAuth() {
	const ctx = useContext(ThirdPartyAuthContext);
	if (!ctx) {
		throw new Error('useThirdPartyAuth must be used within a ThirdPartyAuthProvider');
	}
	return ctx;
}
